/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      Italian fiscal code (codice fiscale) validation — format/checksum
 *              check via codFiscaleValidMsg, plus a name-match check against
 *              first/last name.
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      (none — no imports)
 *   siblings:  geo.js, province.js (sibling utility/ modules, all re-exported
 *              via ../utility.js barrel)
 *   tests:     utility-test.js (unit), utility-extra-test.js (unit)
 *   gotchas:   accented letters (à, è, ò…) must be NFD-folded before
 *              consonant/vowel stripping, else the accented char is dropped
 *              entirely and produces a wrong surname/name code → a false
 *              "does not match" for a valid contact.
 *
 * @module beebee-base/lib/utility/codice-fiscale
 * @description Italian fiscal code (codice fiscale) validation.
 */

/**
 * Valida un codice fiscale italiano
 * @param {string} cod_fiscale - Codice fiscale da validare
 * @param {boolean} fisica_giuridica - True se persona giuridica
 * @param {string} ragione_sociale - Ragione sociale (se azienda)
 * @param {string} location_address_country - Paese
 * @param {string|null} first_name - Nome (opzionale)
 * @param {string|null} last_name - Cognome (opzionale)
 * @returns {string|null} Messaggio di errore o null se valido
 */
function codFiscaleValidMsg(cod_fiscale, fisica_giuridica, ragione_sociale, location_address_country, first_name = null, last_name = null) {
  const codiceFiscale = (cod_fiscale || '').toUpperCase().replace(/\s+/g, '');

  // Skip validation for non-Italian or business entities
  if (!codiceFiscale || fisica_giuridica || ragione_sociale?.trim() || location_address_country && location_address_country.toLowerCase() !== 'italia') {
    return null;
  }
  if (codiceFiscale.length !== 16) {
    return codiceFiscale.length < 16 ? 'tel.too_short' : 'tel.too_long';
  }
  const codiceFiscaleNoControl = codiceFiscale.substring(0, 15);
  // Omocodia: le 7 posizioni numeriche possono contenere le lettere L-V al posto
  // delle cifre 0-9 quando il codice standard collide con uno già assegnato.
  const omocodiaDigit = '[0-9LMNPQRSTUV]';
  const codiceFiscaleRegex = new RegExp(`^[A-Z]{6}${omocodiaDigit}{2}[A-Z]${omocodiaDigit}{2}[A-Z]${omocodiaDigit}{3}[A-Z]$`);
  if (!codiceFiscaleRegex.test(codiceFiscale) || !isValidCodiceFiscale(codiceFiscaleNoControl, codiceFiscale.charAt(15))) {
    return 'Invalid';
  }
  if (first_name && last_name) {
    const lnCode = getSurnameCode(last_name);
    const fnCode = getNameCode(first_name);
    const start = lnCode + fnCode;
    if (!codiceFiscale.startsWith(start)) {
      return 'The tax code does not seem to match the contact';
    }
  }
  function getSurnameCode(surname) {
    const surnameCode = (getConsonants(surname) + getVowels(surname) + 'XXX').slice(0, 3);
    return (surnameCode || '').toUpperCase();
  }
  function getNameCode(name) {
    let nameCode = getConsonants(name);
    nameCode = nameCode.length >= 4 ? nameCode[0] + nameCode[2] + nameCode[3] : (nameCode + getVowels(name) + 'XXX').slice(0, 3);
    return nameCode.toUpperCase();
  }

  // Fold accented letters (à, è, ò…) to their base form BEFORE stripping, else
  // the accented char is dropped entirely → wrong surname/name code → false
  // "codice fiscale does not match" for a valid contact.
  function getConsonants(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^BCDFGHJKLMNPQRSTVWXYZ]/gi, '');
  }
  function getVowels(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^AEIOU]/gi, '');
  }
  return null;
}
function isValidCodiceFiscale(codiceFiscaleNoControl, carattereControllo) {
  const pariDispari = {
    A: [0, 1],
    0: [0, 1],
    B: [1, 0],
    1: [1, 0],
    C: [2, 5],
    2: [2, 5],
    D: [3, 7],
    3: [3, 7],
    E: [4, 9],
    4: [4, 9],
    F: [5, 13],
    5: [5, 13],
    G: [6, 15],
    6: [6, 15],
    H: [7, 17],
    7: [7, 17],
    I: [8, 19],
    8: [8, 19],
    J: [9, 21],
    9: [9, 21],
    K: [10, 2],
    L: [11, 4],
    M: [12, 18],
    N: [13, 20],
    O: [14, 11],
    P: [15, 3],
    Q: [16, 6],
    R: [17, 8],
    S: [18, 12],
    T: [19, 14],
    U: [20, 16],
    V: [21, 10],
    W: [22, 22],
    X: [23, 25],
    Y: [24, 24],
    Z: [25, 23]
  };
  const controlloArr = {
    0: 'A',
    1: 'B',
    2: 'C',
    3: 'D',
    4: 'E',
    5: 'F',
    6: 'G',
    7: 'H',
    8: 'I',
    9: 'J',
    10: 'K',
    11: 'L',
    12: 'M',
    13: 'N',
    14: 'O',
    15: 'P',
    16: 'Q',
    17: 'R',
    18: 'S',
    19: 'T',
    20: 'U',
    21: 'V',
    22: 'W',
    23: 'X',
    24: 'Y',
    25: 'Z'
  };
  const sommaPariDispari = codiceFiscaleNoControl.split('').reduce((acc, char, index) => {
    if (index % 2 === 0) {
      // Posizione dispari
      acc += pariDispari[char][1];
    } else {
      // Posizione pari
      acc += pariDispari[char][0];
    }
    return acc;
  }, 0);
  const resto = sommaPariDispari % 26;
  const controlLetter = controlloArr[resto];
  return carattereControllo === controlLetter;
}

export { codFiscaleValidMsg };
//# sourceMappingURL=codice-fiscale.js.map
